import { ActionIcon, Badge, Indicator, Menu, Popover, SimpleGrid, Stack, Text, Tooltip, UnstyledButton } from '@mantine/core'
import { useDebouncedValue } from '@mantine/hooks'
import { Spotlight, spotlight } from '@mantine/spotlight'
import {
  IconAdjustmentsHorizontal,
  IconBuildingSkyscraper,
  IconCloud,
  IconDownload,
  IconGridDots,
  IconHome2,
  IconLogout,
  IconReceipt2,
  IconSearch,
  IconShieldLock,
  IconSteeringWheel,
  IconTruck,
  IconUserCircle,
  IconUsersGroup,
  IconWallet,
} from '@tabler/icons-react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { type AppIcon, type AppId, apps, labels, type RefMap, type ResolvedField } from '@tnn/core'
import { appsFor, findResource } from '@tnn/domain'
import { currentLocale } from '@tnn/i18n'
import { type IconComponent, ResponsiveDialog } from '@tnn/ui'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { api } from '../api/client'
import type { Rec } from '../api/resources'
import { useSession } from '../auth/session'
import { PersonAvatar } from '../photo/photos'
import { PreferencesForm } from '../prefs/preferences'
import { useInstallApp } from '../pwa/app'
import { pagePath, type ResourcePage } from '../resource/config'
import { fieldText } from '../resource/FieldValue'
import classes from './chrome.module.css'
import { goToApp } from './go'
import { appLink } from './links'
import { useNavigate } from './scope'
import { useWaitingByApp } from './waiting'

export const appIcons: Record<AppIcon, IconComponent> = {
  home: IconHome2,
  truck: IconTruck,
  receipt: IconReceipt2,
  users: IconUsersGroup,
  shield: IconShieldLock,
  wallet: IconWallet,
  building: IconBuildingSkyscraper,
  wheel: IconSteeringWheel,
  cloud: IconCloud,
}

/** Generated per-app pictures, cut from one sheet by `pnpm icons` (scripts/app-icons.mjs). */
const appImages = import.meta.glob<string>('./icons/*.png', { eager: true, import: 'default' })

/** An app's generated picture (its menus, and the browser tab's icon while it is open), if it has one */
export const appImage = (id: AppId): string | undefined => appImages[`./icons/${id}.png`]

/** An app's icon: its generated picture, or its seed colour and glyph until it has one. */
export function AppMark({ id, size, count }: { id: AppId; size: number; count?: number }) {
  const src = appImage(id)
  const Icon = appIcons[apps[id].icon]
  const mark = src ? (
    <img src={src} width={size} height={size} alt="" className={classes.appMark} />
  ) : (
    <span className={classes.appMark} style={{ width: size, height: size, background: apps[id].seed }}>
      <Icon size={size * 0.5} stroke={1.9} />
    </span>
  )
  if (count === undefined) return mark
  // What waits for one in that app (`useWaitingByApp`): a red pill on the icon's corner, red in either scheme; a smaller
  // one on a small icon (the top bar's tabs)
  const small = size <= 24
  return (
    <Indicator
      label={count > 99 ? '99+' : count}
      disabled={!count}
      color="danger.6"
      size={small ? 14 : 18}
      offset={size * 0.1}
      classNames={small ? { indicator: classes.smallCount } : undefined}
      inline
    >
      {mark}
    </Indicator>
  )
}

/** Apps the signed-in user is shown, portal first (`appsFor`: Driver in place of TMS for a truck's crew). */
export function useAccessibleApps(): AppId[] {
  const { me, ability } = useSession()
  return appsFor({ ability, driverId: me.driverId, employeeId: me.employeeId, permissions: me.permissions })
}

/** Google-style 3×3 launcher for the apps; another app loads in place of this one (`appLink`). */
export function AppSwitcher() {
  const { t } = useTranslation()
  const list = useAccessibleApps()
  const waiting = useWaitingByApp()
  return (
    <Popover position="bottom-end" width={300} radius="lg" shadow="md">
      <Popover.Target>
        <Tooltip label={t('apps.switcher')}>
          <ActionIcon size={40} aria-label={t('apps.switcher')}>
            <IconGridDots size={22} stroke={2} />
          </ActionIcon>
        </Tooltip>
      </Popover.Target>
      <Popover.Dropdown p="sm">
        <SimpleGrid cols={3} spacing={4}>
          {list.map((id) => (
            <UnstyledButton key={id} component="a" href={appLink(id)} className={classes.appTile}>
              <AppMark id={id} size={44} count={waiting[id] ?? 0} />
              <Text size="xs" ta="center" lineClamp={1}>
                {t(`apps.${id}`)}
              </Text>
            </UnstyledButton>
          ))}
        </SimpleGrid>
      </Popover.Dropdown>
    </Popover>
  )
}

/** Avatar with the account menu: my account, preferences, install, portal, sign out. */
export function AccountMenu() {
  const { t } = useTranslation()
  const { me, signOut } = useSession()
  const install = useInstallApp()
  const [prefsOpen, setPrefsOpen] = useState(false)
  return (
    <>
      <Menu width={300} position="bottom-end" radius="lg">
        <Menu.Target>
          <UnstyledButton aria-label={me.user.name} className={classes.avatarButton}>
            <PersonAvatar name={me.user.name} fileId={me.user.image} size={34} />
          </UnstyledButton>
        </Menu.Target>
        <Menu.Dropdown>
          <Stack align="center" gap={2} p="md" pb="sm">
            <PersonAvatar name={me.user.name} fileId={me.user.image} size={64} />
            <Text fw={500} mt="xs">
              {me.user.name}
            </Text>
            <Text size="sm" c="dimmed">
              {me.user.username}
            </Text>
            {me.roles.length > 0 && (
              <Text size="xs" c="dimmed" ta="center">
                {me.roles.map((r) => r.name).join(' · ')}
              </Text>
            )}
          </Stack>
          <Menu.Divider />
          {/* My account lives in Thananan OS: from any other app it opens there */}
          <Menu.Item leftSection={<IconUserCircle size={18} stroke={1.75} />} component="a" href={appLink('portal', '/account')}>
            {t('prefs.profile')}
          </Menu.Item>
          <Menu.Item leftSection={<IconAdjustmentsHorizontal size={18} stroke={1.75} />} onClick={() => setPrefsOpen(true)}>
            {t('prefs.title')}
          </Menu.Item>
          {install && (
            <Menu.Item leftSection={<IconDownload size={18} stroke={1.75} />} onClick={install}>
              {t('pwa.install')}
            </Menu.Item>
          )}
          <Menu.Item leftSection={<IconHome2 size={18} stroke={1.75} />} component="a" href={appLink('portal')}>
            {t('apps.portal')}
          </Menu.Item>
          <Menu.Item leftSection={<IconLogout size={18} stroke={1.75} />} onClick={signOut}>
            {t('auth.signOut')}
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
      <ResponsiveDialog opened={prefsOpen} onClose={() => setPrefsOpen(false)} title={t('prefs.title')} size="md">
        <PreferencesForm />
      </ResponsiveDialog>
    </>
  )
}

export function DemoBadge() {
  const { t } = useTranslation()
  return (
    <Badge color="warning" variant="light" radius="sm" size="md" ml={4} style={{ textTransform: 'none', fontWeight: 500, flex: 'none' }}>
      {t('status.demo')}
    </Badge>
  )
}

export interface CommandAction {
  id: string
  label: string
  description?: string
  icon?: IconComponent
  href: string
}

/** What `/api/search` found in one resource: a few records with what the search box shows of them */
interface SearchGroup {
  resource: string
  items: Rec[]
  refs: RefMap
}

/**
 * ⌘K / Ctrl K: jump to any screen, or find records as you type: this app's first, then those of every other app one
 * may open, each at its own page there (a shared module's, such as cash, only in the apps that show it)
 */
export function CommandPalette({ actions, pages }: { actions: CommandAction[]; pages: ResourcePage[] }) {
  const { t, i18n } = useTranslation()
  const locale = currentLocale(i18n)
  const navigate = useNavigate()
  const { appId } = useSession()
  const openable = useAccessibleApps()
  const [query, setQuery] = useState('')
  const [q] = useDebouncedValue(query.trim(), 200)
  const { data } = useQuery({
    queryKey: ['search', q],
    queryFn: () => api.get<{ groups: SearchGroup[] }>(`/search?${new URLSearchParams({ q })}`),
    enabled: q.length >= 2,
    staleTime: 10_000,
    // What was found stays while the next word is looked up
    placeholderData: keepPreviousData,
  })

  const here = pages.map((p) => p.resource.key)
  // This app's in its menu's order, then the other apps' as the app switcher shows them
  const rank = (key: string, app: AppId) => (app === appId ? here.indexOf(key) : here.length + openable.indexOf(app))
  const groups = (q.length >= 2 ? (data?.groups ?? []) : [])
    .flatMap(({ resource: key, items, refs }) => {
      const resource = findResource(key)
      if (!resource) return []
      const home = resource.app in apps ? (resource.app as AppId) : undefined
      const app = here.includes(key) ? appId : home && home !== appId && openable.includes(home) ? home : undefined
      return app ? [{ resource, items, refs, app }] : []
    })
    .sort((a, b) => rank(a.resource.key, a.app) - rank(b.resource.key, b.app))

  const go = (app: AppId, href: string) => {
    spotlight.close()
    if (app === appId) navigate(href)
    else goToApp(appLink(app, href))
  }
  const filtered = actions.filter((a) => !query || a.label.toLowerCase().includes(query.toLowerCase()))

  return (
    <Spotlight.Root
      query={query}
      onQueryChange={setQuery}
      shortcut={null}
      scrollable
      maxHeight={520}
      radius="lg"
      overlayProps={{ backgroundOpacity: 0.32, blur: 0 }}
      classNames={{ actionsGroup: classes.searchGroup }}
    >
      <Spotlight.Search placeholder={t('shell.searchPlaceholder')} leftSection={<IconSearch size={20} stroke={1.75} />} />
      <Spotlight.ActionsList>
        {filtered.length > 0 && (
          <Spotlight.ActionsGroup label={t('shell.goTo')}>
            {filtered.map((a) => (
              <Spotlight.Action
                key={a.id}
                label={a.label}
                description={a.description}
                leftSection={a.icon && <a.icon size={20} stroke={1.75} />}
                onClick={() => go(appId, a.href)}
              />
            ))}
          </Spotlight.ActionsGroup>
        )}
        {groups.map(({ resource: r, items, refs, app }) => {
          const titleField = r.field(r.title) as ResolvedField
          // The card's title and subtitle say which record it is (customer · destination)
          const details = [r.card?.title, r.card?.subtitle]
            .filter((name) => name && name !== r.title)
            .flatMap((name) => r.field(name as string) ?? [])
          const ctx = { resource: r, refs, t, locale }
          // Another app's records say which app they open in
          const label = app === appId ? t(labels.plural(r)) : `${t(labels.plural(r))} · ${t(`apps.${app}`)}`
          return (
            <Spotlight.ActionsGroup key={r.key} label={label}>
              {items.map((item) => (
                <Spotlight.Action
                  key={item.id}
                  label={fieldText(titleField, item[titleField.name], ctx)}
                  description={details
                    .map((f) => fieldText(f, item[f.name], ctx))
                    .filter(Boolean)
                    .join(' · ')}
                  leftSection={app === appId ? undefined : <AppMark id={app} size={24} />}
                  onClick={() => go(app, `/${pagePath(r)}/${item.id}`)}
                />
              ))}
            </Spotlight.ActionsGroup>
          )
        })}
        {filtered.length === 0 && groups.length === 0 && (
          <Spotlight.Empty>{q.length >= 2 && data ? t('empty.noResults') : t('form.searchPlaceholder')}</Spotlight.Empty>
        )}
      </Spotlight.ActionsList>
    </Spotlight.Root>
  )
}
